import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import AppHeader from '../components/AppHeader'
import { useAuth } from '../contexts/AuthContext'
import { notificationService } from '../services/notificationService'
import { 
  BellIcon, 
  XMarkIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  TrashIcon
} from '@heroicons/react/24/outline'

interface Notification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  timestamp: Date
  read: boolean
  actionUrl?: string
}

const NotificationHistoryPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'unread' | 'success' | 'warning' | 'error' | 'info'>('all')
  const [loading, setLoading] = useState(true)

  // Load real notifications from the database
  const loadNotifications = async () => {
    if (!user) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const result = await notificationService.getNotifications(user.id)
      if (result.success && result.notifications) {
        setNotifications(result.notifications)
      } else {
        console.error('Failed to load notifications:', result.error)
        // Show welcome message for new users with no notifications
        setNotifications([
          {
            id: 'welcome',
            type: 'info' as const,
            title: 'Welcome to ShootSuite! 🎉',
            message: 'You\'ll see notifications here when you have invoices, shoots, or other important updates. Start by creating your first client or invoice!',
            timestamp: new Date(),
            read: false,
            actionUrl: '/clients'
          }
        ])
      }
    } catch (error) {
      console.error('Error loading notifications:', error)
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications()
  }, [user])

  // Filter notifications based on search and filter type
  useEffect(() => {
    let filtered = notifications

    // Apply type filter
    if (filterType === 'unread') {
      filtered = filtered.filter(n => !n.read)
    } else if (filterType !== 'all') {
      filtered = filtered.filter(n => n.type === filterType)
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.message.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    setFilteredNotifications(filtered)
  }, [notifications, searchQuery, filterType])

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-6 h-6 text-emerald-400" />
      case 'warning':
        return <ExclamationTriangleIcon className="w-6 h-6 text-yellow-400" />
      case 'error':
        return <XMarkIcon className="w-6 h-6 text-red-400" />
      default:
        return <InformationCircleIcon className="w-6 h-6 text-blue-400" />
    }
  }

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date()
    const diff = now.getTime() - timestamp.getTime()
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (minutes < 60) {
      return `${minutes}m ago`
    } else if (hours < 24) {
      return `${hours}h ago`
    } else if (days === 1) {
      return 'Yesterday'
    } else if (days < 7) {
      return `${days}d ago`
    } else {
      return timestamp.toLocaleDateString()
    }
  }

  const markAsRead = async (id: string) => {
    // Skip API call for welcome message
    if (id === 'welcome') {
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      )
      return
    }

    try {
      const result = await notificationService.markAsRead(id)
      if (result.success) {
        setNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, read: true } : n)
        )
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    )
  }

  const deleteNotification = async (id: string) => {
    // Skip API call for welcome message
    if (id === 'welcome') {
      setNotifications(prev => prev.filter(n => n.id !== id))
      return
    }

    try {
      const result = await notificationService.deleteNotification(id)
      if (result.success) {
        setNotifications(prev => prev.filter(n => n.id !== id))
      }
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  const deleteAllRead = async () => {
    if (!user) return
    
    try {
      // Get all read notifications
      const readNotifications = notifications.filter(n => n.read && n.id !== 'welcome')
      
      if (readNotifications.length === 0) return
      
      // Delete all read notifications from database
      const promises = readNotifications.map(notification => 
        notificationService.deleteNotification(notification.id)
      )
      
      const results = await Promise.allSettled(promises)
      const successful = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length
      
      console.log(`Deleted ${successful} read notifications, ${failed} failed`)
      
      // Update local state
      setNotifications(prev => prev.filter(n => !n.read || n.id === 'welcome'))
    } catch (error) {
      console.error('Error deleting read notifications:', error)
    }
  }

  const deleteAllNotifications = async () => {
    if (!user) return
    
    try {
      // Get all notifications except welcome message
      const realNotifications = notifications.filter(n => n.id !== 'welcome')
      
      if (realNotifications.length === 0) return
      
      // Delete all notifications from database
      const promises = realNotifications.map(notification => 
        notificationService.deleteNotification(notification.id)
      )
      
      const results = await Promise.allSettled(promises)
      const successful = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length
      
      console.log(`Deleted ${successful} notifications, ${failed} failed`)
      
      // Update local state - keep only welcome message if it exists
      setNotifications(prev => prev.filter(n => n.id === 'welcome'))
    } catch (error) {
      console.error('Error deleting all notifications:', error)
    }
  }

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader 
        title="Notifications" 
        subtitle={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'All caught up!'}
      >
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={loadNotifications}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-xl hover:bg-white/10"
            title="Refresh notifications"
          >
            🔄
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/dashboard')}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-xl hover:bg-white/10"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </motion.button>
        </div>
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-mockup rounded-2xl p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter Dropdown */}
            <div className="relative">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="appearance-none bg-white/10 border border-white/20 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all" className="bg-gray-800">All Notifications</option>
                <option value="unread" className="bg-gray-800">Unread Only</option>
                <option value="success" className="bg-gray-800">Success</option>
                <option value="warning" className="bg-gray-800">Warnings</option>
                <option value="error" className="bg-gray-800">Errors</option>
                <option value="info" className="bg-gray-800">Info</option>
              </select>
              <FunnelIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={markAllAsRead}
                  className="px-4 py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-xl transition-colors border border-blue-500/30"
                >
                  Mark All Read
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={deleteAllRead}
                className="px-4 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-colors border border-red-500/30"
                title="Delete all read notifications"
              >
                <TrashIcon className="w-5 h-5" />
              </motion.button>
              {notifications.filter(n => n.id !== 'welcome').length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={deleteAllNotifications}
                  className="px-4 py-3 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded-xl transition-colors border border-red-600/30"
                  title="Clear all notifications"
                >
                  Clear All
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Notifications List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-4"
        >
          {loading ? (
            <div className="glass-mockup rounded-2xl p-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="glass-mockup rounded-2xl p-12 text-center">
              <BellIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {notifications.length === 0 ? 'No notifications yet' : 'No matching notifications'}
              </h3>
              <p className="text-gray-400 mb-6">
                {notifications.length === 0 
                  ? "You'll receive notifications here when important events happen in your business."
                  : 'Try adjusting your search or filter criteria.'
                }
              </p>
              {notifications.length === 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary"
                >
                  Back to Dashboard
                </motion.button>
              )}
            </div>
          ) : (
            filteredNotifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`glass-mockup rounded-2xl p-6 transition-all cursor-pointer group ${
                  notification.read 
                    ? 'hover:bg-white/10' 
                    : 'bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20'
                }`}
                onClick={() => {
                  markAsRead(notification.id)
                  if (notification.actionUrl) {
                    navigate(notification.actionUrl)
                  }
                }}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-white font-semibold">{notification.title}</h3>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                        <p className="text-gray-400 mb-3">{notification.message}</p>
                        <div className="flex items-center text-sm text-gray-500">
                          <ClockIcon className="w-4 h-4 mr-2" />
                          {formatTimestamp(notification.timestamp)}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteNotification(notification.id)
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-white/10 rounded-lg"
                      >
                        <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </motion.div>

        {/* Upcoming: Automated Email Sequences */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <BellIcon className="w-6 h-6 text-emerald-400" />
            <h2 className="text-xl font-bold text-white">Upcoming: Automated Email Sequences</h2>
          </div>
          <div className="glass-badge rounded-xl p-4 text-white/80 bg-emerald-900/30 border-l-4 border-emerald-500 mb-2">
            <strong>Automated onboarding, updates, and follow-up emails</strong> with A/B testing are coming soon! Manage and optimize your client communications right here.
          </div>
        </div>
      </main>
    </div>
  )
}

export default NotificationHistoryPage 